import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import type { IAccount, IUserInfo } from '../../interfaces';
import { RocosClientService } from '../rocos-client';
import { AppService, ProjectFavoritesService, RocosSdkClientService } from '@shared/services';

export const KEY_ACCESS_TOKEN = 'access_token';
export const KEY_ID_TOKEN = 'id_token';
export const KEY_TOKEN = 'token';
export const KEY_USER_INFO = 'user_info';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public userUpdated = new Subject<IUserInfo>();
  public loggedOut = new Subject<IUserInfo>();

  public userInfo: IUserInfo;

  public readonly token$: Observable<string | null>;

  public constructor(
    private app: AppService,
    private rocosClientService: RocosClientService,
    private rocosSdkClient: RocosSdkClientService,
    private favoritesService: ProjectFavoritesService,
  ) {
    this.token$ = this.rocosSdkClient.client$.pipe(
      map((client) => client.getAuthService()),
      tap((auth) => auth.startTokenRefreshChecker()),
      switchMap((auth) => auth.token$),
      map((token) => token.value),
    );

    this.retrievePayload();

    if (this.userInfo?.token) {
      this.rocosSdkClient.login(this.userInfo.token);
    }

    this.token$.subscribe((token) => {
      this.updateSessionByToken(token);
    });
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return !!this.userInfo?.token;
  }

  public hasAccount(): boolean {
    return this.userInfo?.accounts?.length > 0;
  }

  /**
   * Logout
   */
  public logout() {
    this.loggedOut.next(this.userInfo);
    this.clearSession();
  }

  /**
   * Callback for handling login
   *
   * @param userInfo Logged in user info
   */
  public handleLogin(userInfo: IUserInfo) {
    if (!userInfo?.token) {
      this.clearSession();
      throw new Error('invalid user info: no token provided');
    }

    this.updateSessionByUserInfo(userInfo);
  }

  public updateNewToken(token: string): void {
    this.updateSessionByToken(token);
    this.rocosSdkClient.login(token);
  }

  /**
   * Update accounts of userInfo
   *
   * @param accounts Accounts
   */
  public updateAccountsToUserInfo(accounts: IAccount[]): void {
    if (this.userInfo) {
      this.userInfo.accounts = accounts;
    }
  }

  /**
   * Update the session by user info
   *
   * @param userInfo Logged in user info
   */
  public updateSessionByUserInfo(userInfo: IUserInfo): void {
    localStorage.setItem(KEY_TOKEN, userInfo.token);
    localStorage.setItem(KEY_USER_INFO, JSON.stringify(userInfo));
    this.retrievePayload();

    this.rocosSdkClient.login(userInfo.token);

    this.userUpdated.next(this.userInfo);
  }

  public loginWithOneTimeToken(token: string) {
    this.clearSession();
    return this.rocosClientService.rocosClient.user.loginWithOnetimeToken(token);
  }

  private updateSessionByToken(token: string): void {
    localStorage.setItem(KEY_TOKEN, token);
    if (this.userInfo) {
      this.userInfo.token = token;
    }
    localStorage.setItem(KEY_USER_INFO, JSON.stringify(this.userInfo));

    this.rocosClientService.updateToken(token);

    this.retrievePayload();
  }

  private clearSession(): void {
    localStorage.removeItem(KEY_ACCESS_TOKEN);
    localStorage.removeItem(KEY_ID_TOKEN);
    localStorage.removeItem(KEY_TOKEN);
    localStorage.removeItem(KEY_USER_INFO);

    // Remove all local storages, except for a few things.
    const redirect = localStorage.getItem('redirect_url');
    const favorites = this.favoritesService.getFavoritesRaw();
    localStorage.clear();
    if (redirect) {
      localStorage.setItem('redirect_url', redirect);
    }
    if (favorites) {
      this.favoritesService.setFavoritesRaw(favorites);
    }

    // Remove the token from rocos client
    this.rocosClientService.rocosClient.removeToken();

    const authService = this.rocosSdkClient?.client?.getAuthService?.();
    if (authService) {
      authService.stopTokenRefreshChecker();
      authService.clearToken();
    }

    this.retrievePayload();
  }

  private retrievePayload(): void {
    try {
      const userInfoString = localStorage.getItem(KEY_USER_INFO);
      if (userInfoString?.length) {
        this.userInfo = JSON.parse(userInfoString);
        this.updateActiveAccount();
      } else {
        this.userInfo = null;
      }
    } catch (e) {
      console.error('Error while parsing user info', e);
      throw e;
    }
  }

  private updateActiveAccount(): void {
    const activeAccountId = this.userInfo?.activeAccount?.id;
    if (this.app.accountId !== undefined || !activeAccountId) {
      return;
    }

    this.app.updateAccountId(activeAccountId);
  }
}
